import React, { useState, useEffect } from 'react';
import { API } from 'aws-amplify';
import { GRAPHQL_AUTH_MODE } from '@aws-amplify/api-graphql';

const BADGE_COLOR = '#3b3b79';

/**
 * Cuando el endpoint GET /company-verification sea público (sin JWT),
 * cambiar este flag a `true` para que el badge consulte la fuente de verdad
 * en tiempo real en lugar de DynamoDB.
 */
const USE_REALTIME_API = true;
const VERIFICATION_API_URL = 'https://files.bainub.com/api/v1';

/** Query mínima — solo los campos necesarios para reducir el payload. */
const GET_COMPANY_VERIFIED = /* GraphQL */ `
    query GetCompanyVerified($id: ID!) {
        getCompany(id: $id) {
            id
            verified
        }
    }
`;

/**
 * Caché en memoria por sesión de navegador.
 * Previene requests duplicados cuando múltiples instancias del componente
 * solicitan el estado del mismo companyId (p. ej. listados de búsqueda).
 */
const verificationCache = new Map();

/**
 * VerifiedBadge
 *
 * Componente autónomo que muestra la insignia de empresa verificada.
 * Resuelve su propio estado de verificación a partir del `companyId`.
 *
 * @param {string}  companyId  - ID de la empresa. Uso recomendado.
 * @param {boolean} isVerified - Valor directo. Útil cuando el padre
 *                               ya conoce el estado (evita el fetch).
 * @param {object}  style      - Estilos adicionales para el wrapper.
 * @param {number}  iconSize   - Tamaño del ícono SVG en px.
 * @param {string}  fontSize   - Tamaño de la fuente del texto.
 */
export default function VerifiedBadge({
    companyId,
    isVerified,
    style,
    iconSize = 14,
    fontSize = '12px'
}) {
    const directValue =
        isVerified === true || isVerified === 1 || isVerified === 'true';

    const [verified, setVerified] = useState(directValue ? true : null);

    useEffect(() => {
        // Si el padre conoce que es verdadero, evitamos la red.
        // Si el valor precargado es falso o indefinido, le damos una segunda oportunidad
        // consultando la fuente de verdad en tiempo real.
        if (directValue) {
            setVerified(true);
            return;
        }
        if (!companyId) return;

        if (verificationCache.has(companyId)) {
            setVerified(verificationCache.get(companyId));
            return;
        }

        let cancelled = false;

        const fetchVerificationStatus = async () => {
            try {
                let isVerifiedResult = false;

                if (USE_REALTIME_API) {
                    // Consulta directa a la API de verificación (requiere endpoint público).
                    const response = await fetch(
                        `${VERIFICATION_API_URL}/company-verification?company_id=${companyId}`
                    );
                    if (response.ok) {
                        const data = await response.json();
                        if (data.success && data.data) {
                            isVerifiedResult = data.data.some(r => r.company_verified === true);
                        }
                    }
                } else {
                    // Consulta el campo `verified` desde DynamoDB via GraphQL con
                    // autenticación IAM, accesible públicamente sin requerir sesión.
                    const graphqlResult = await API.graphql({
                        query: GET_COMPANY_VERIFIED,
                        variables: { id: companyId },
                        authMode: GRAPHQL_AUTH_MODE.AWS_IAM,
                    });
                    const v = graphqlResult.data?.getCompany?.verified;
                    isVerifiedResult = v === 1 || v === true;
                }

                if (!cancelled) {
                    verificationCache.set(companyId, isVerifiedResult);
                    setVerified(isVerifiedResult);
                }
            } catch (err) {
                console.warn('VerifiedBadge: error al obtener estado de verificación', err);
                if (!cancelled) {
                    verificationCache.set(companyId, false);
                    setVerified(false);
                }
            }
        };

        fetchVerificationStatus();

        return () => { cancelled = true; };
    }, [companyId, isVerified]);

    if (!verified) return null;

    return (
        <span style={{
            display: 'inline-flex',
            alignItems: 'center',
            marginLeft: '6px',
            color: BADGE_COLOR,
            fontWeight: '700',
            fontSize: fontSize,
            fontStyle: 'italic',
            ...style
        }}>
            <svg
                xmlns="http://www.w3.org/2000/svg"
                width={iconSize}
                height={iconSize}
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                style={{ marginRight: '4px' }}
            >
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                <polyline points="22 4 12 14.01 9 11.01"></polyline>
            </svg>
            Verified
        </span>
    );
}